import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Save, X, UserPlus } from 'lucide-react';
import SearchableDropdown from './SearchableDropdown';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({
    managers: [],
    departments: [],
    roles: []
  });
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    roles: [],
    manager: '',
    department: '',
    team: ''
  });

  // Filtered data based on team selection
  const [filteredRoles, setFilteredRoles] = useState([]);

  useEffect(() => {
    fetchEmployees();
    fetchDropdownOptions();
    fetchTeamsAndRoles();
    fetchEmployeeNames();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const response = await fetch('/api/dropdown-options');
      if (!response.ok) throw new Error('Failed to fetch dropdown options');
      const data = await response.json();
      setDropdownOptions(data);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  const fetchTeamsAndRoles = async () => {
    try {
      const [teamsResponse, rolesResponse] = await Promise.all([
        fetch('/api/teams'),
        fetch('/api/roles')
      ]);

      if (teamsResponse.ok && rolesResponse.ok) {
        const teamsData = await teamsResponse.json();
        const rolesData = await rolesResponse.json();
        console.log('Teams data:', teamsData);
        console.log('Roles data:', rolesData);
        
        // Map the data to the expected format
        const mappedTeams = teamsData.map(team => ({
          id: team.TeamID,
          name: team.Title,
          manager: team.Manager,
          director: team.Director
        }));
        
        const mappedRoles = rolesData.map(role => ({
          id: role.RoleID,
          name: role.Title,
          team: role.TeamName,
          teamId: role.TeamID,
          owner: role.Owner
        }));
        
        console.log('Mapped teams:', mappedTeams);
        console.log('Mapped roles:', mappedRoles);
        
        setTeams(mappedTeams);
        setRoles(mappedRoles);
      } else {
        console.error('Failed to fetch teams or roles:', {
          teamsStatus: teamsResponse.status,
          rolesStatus: rolesResponse.status
        });
      }
    } catch (error) {
      console.error('Error fetching teams and roles:', error);
    }
  };

  const fetchEmployeeNames = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (response.ok) {
        const employeesData = await response.json();
        const names = employeesData.map(emp => emp.name).sort();
        setEmployeeNames(names);
      }
    } catch (error) {
      console.error('Error fetching employee names:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      setError('Employee name is required');
      return;
    }

    if (!formData.team) {
      setError('Team selection is required');
      return;
    }

    if (formData.roles.length === 0) {
      setError('At least one role is required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        roles: formData.roles,
        role: formData.roles[0], // Primary role
        manager: formData.manager,
        department: formData.department
      };

      const url = editingEmployee 
        ? `/api/employees/${editingEmployee.id}`
        : '/api/employees';
      
      const method = editingEmployee ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save employee');
      }

      await fetchEmployees();
      resetForm();
      setError(null);
    } catch (error) {
      console.error('Error saving employee:', error);
      setError(error.message);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    
    // Find the team based on the employee's department
    const team = teams.find(t => t.name === employee.department);
    const teamName = team ? team.name : '';
    
    setFormData({
      name: employee.name,
      roles: employee.roles || [],
      manager: employee.manager,
      department: employee.department,
      team: teamName
    });

    // Set filtered roles based on the team - include all roles for editing
    if (team) {
      const teamRoles = roles.filter(role => role.team === teamName);
      const teamRoleNames = teamRoles.map(role => role.name);
      // Include existing employee roles that might not be in the team filter
      const existingRoles = employee.roles || [];
      const allAvailableRoles = [...new Set([...teamRoleNames, ...existingRoles])];
      setFilteredRoles(allAvailableRoles);
    } else {
      // If no team found, allow all existing roles plus general roles
      setFilteredRoles(employee.roles || []);
    }
    
    setShowAddForm(true);
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    try {
      const response = await fetch(`/api/employees/${employeeId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete employee');
      }

      await fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      setError(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      roles: [],
      manager: '',
      department: '',
      team: ''
    });
    setFilteredRoles([]);
    setShowAddForm(false);
    setEditingEmployee(null);
  };

  const handleTeamChange = (selectedTeam) => {
    const team = teams.find(t => t.name === selectedTeam);
    if (team) {
      // Auto-populate manager and department
      setFormData(prev => ({
        ...prev,
        team: selectedTeam,
        manager: team.manager,
        department: team.name,
        roles: [] // Reset roles when team changes
      }));

      // Filter roles based on team
      const teamRoles = roles.filter(role => role.team === selectedTeam);
      setFilteredRoles(teamRoles.map(role => role.name));
    } else {
      setFormData(prev => ({
        ...prev,
        team: selectedTeam,
        roles: []
      }));
      setFilteredRoles([]);
    }
  };

  const handleRoleAdd = (role) => {
    if (role && !formData.roles.includes(role)) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    }
  };

  const handleRoleRemove = (roleToRemove) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.filter(role => role !== roleToRemove)
    }));
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="loading-spinner"></div>
        <div>Loading employees...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="header">
        <div className="header-text">
          <h1>Employee Management</h1>
          <p>Add, edit, and manage employees with multiple roles</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowAddForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            backgroundColor: '#6c5ce7',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <UserPlus size={18} />
          Add Employee
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          color: '#d63031'
        }}>
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: 0 }}>
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button
                onClick={resetForm}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Employee Name *
                </label>
                <SearchableDropdown
                  options={employeeNames}
                  value={formData.name}
                  placeholder="Select or type employee name..."
                  onChange={(name) => setFormData(prev => ({ ...prev, name }))}
                  allowCustom={true}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Team *
                </label>
                <SearchableDropdown
                  options={teams.map(team => team.name)}
                  value={formData.team}
                  placeholder="Select a team first..."
                  onChange={handleTeamChange}
                  allowCustom={false}
                />
                {formData.team && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                  </div>
                )}
              </div>

              {formData.team && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Manager
                    </label>
                    <input
                      type="text"
                      value={formData.manager}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: '#f8f9fa',
                        color: '#666'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Team
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: '#f8f9fa',
                        color: '#666'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Roles *
                    </label>
                    <SearchableDropdown
                      options={filteredRoles}
                      placeholder="Select roles for this team..."
                      onChange={handleRoleAdd}
                      allowCustom={true}
                    />
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {formData.roles.map((role, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#6c5ce7',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {role}
                          <button
                            type="button"
                            onClick={() => handleRoleRemove(role)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              padding: '0',
                              marginLeft: '4px'
                            }}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    {filteredRoles.length === 0 && formData.team && (
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '8px', 
                        backgroundColor: '#fff3cd', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#856404'
                      }}>
                        No roles found for this team. You can still add custom roles.
                      </div>
                    )}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '12px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    backgroundColor: '#6c5ce7',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Save size={16} />
                  {editingEmployee ? 'Update Employee' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee List */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          background: '#f8f9fa'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            All Employees ({employees.length})
          </h2>
        </div>

        <div style={{ padding: '24px' }}>
          {employees.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#666'
            }}>
              <Users size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Employees Found</h3>
              <p style={{ margin: 0 }}>Add your first employee to get started.</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '16px'
            }}>
              {employees.map(employee => (
                <div
                  key={employee.id}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 4px 0',
                        fontSize: '16px',
                        color: '#333'
                      }}>
                        {employee.name}
                      </h3>
                      <p style={{
                        margin: '0 0 8px 0',
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        {employee.department}
                      </p>
                      <p style={{
                        margin: '0',
                        fontSize: '12px',
                        color: '#888'
                      }}>
                        Manager: {employee.manager}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(employee)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: '#6c5ce7'
                        }}
                        title="Edit Employee"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(employee.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: '#e74c3c'
                        }}
                        title="Delete Employee"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#666',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Roles:
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {employee.roles && employee.roles.length > 0 ? (
                        employee.roles.map((role, index) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: '#6c5ce7',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            {role}
                          </span>
                        ))
                      ) : (
                        <span style={{
                          color: '#999',
                          fontSize: '12px',
                          fontStyle: 'italic'
                        }}>
                          No roles assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeManagement;
